import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { adminGetProduct, adminCreateProduct, adminUpdateProduct } from '../../api/productsApi.js';
import { getCategories, getBrands } from '../../api/catalogApi.js';
import { adminGetOptionTypes } from '../../api/adminCatalogApi.js';
import { getMembershipLevels } from '../../api/membershipLevelsApi.js';
import { useApi } from '../../hooks/useApi.js';
import { useToast } from '../../context/ToastContext.jsx';
import RichTextEditor from '../../components/ui/RichTextEditor.jsx';
import ProductImage from '../../components/ui/ProductImage.jsx';

/**
 * 商品新增 / 編輯共用表單。
 *
 * 路由：
 *   /admin/products/new        → mode='create'
 *   /admin/products/:id/edit   → mode='edit'
 *
 * 選項：先在「設定 → 選項管理」維護選項類型（重量、鏢尖…），
 *       這裡從清單挑這商品用哪些，下面每個規格再照所選的類型填值。
 *       價格 / 庫存都在規格(variant)層。SKU 由後端自動產生。
 */

const EMPTY_VARIANT = { values: {}, price: '', originalPrice: '', stock: 0, active: true, detailHtml: '', imagesText: '' };

const EMPTY_FORM = {
  name: '',
  brand: 'TARGET',
  categoryId: 'barrel',
  description: '',
  detailHtml: '',
  imagesText: 'dart-full',
  specsText: '',
  tags: [],
  featured: false,
  minLevel: 0,
  status: 'draft',
  optionTypes: [],
  variants: [{ ...EMPTY_VARIANT }],
};

const STATUS_OPTIONS = [
  { value: 'active',  label: '上架' },
  { value: 'draft',   label: '草稿' },
  { value: 'soldout', label: '售完' },
];

const TAG_OPTIONS = [
  { value: 'new',  label: '新品' },
  { value: 'best', label: '熱銷' },
  { value: 'hot',  label: '人氣' },
  { value: 'sale', label: '特價' },
];

function AdminProductForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { data: categories = [] }   = useApi(() => getCategories(), []);
  const { data: brands = [] }       = useApi(() => getBrands(), []);
  const { data: optionTypeMaster = [] } = useApi(() => adminGetOptionTypes(), []);
  const { data: levels = [] } = useApi(() => getMembershipLevels(), []);

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);

  // 編輯模式：載入商品資料
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    adminGetProduct(id).then(p => {
      if (cancelled) return;
      if (!p) {
        showToast('找不到該商品', 'error');
        navigate('/admin/products');
        return;
      }
      const variants = (p.variants && p.variants.length > 0)
        ? p.variants.map(v => ({
            values: { ...(v.attrs || {}) },
            price: v.price ?? '',
            originalPrice: v.originalPrice ?? '',
            stock: v.stock ?? 0,
            active: v.active ?? true,
            detailHtml: v.detailHtml || '',
            imagesText: (v.images || []).join('\n'),
          }))
        : [{ ...EMPTY_VARIANT }];
      setForm({
        name: p.name,
        brand: p.brand,
        categoryId: p.categoryId,
        description: p.description || '',
        detailHtml: p.detailHtml || '',
        imagesText: (p.images || []).join('\n'),
        specsText: Object.entries(p.specs || {}).map(([k, v]) => `${k}: ${v}`).join('\n'),
        tags: p.tags || [],
        featured: p.featured ?? false,
        minLevel: p.minLevel ?? 0,
        status: p.status,
        optionTypes: p.optionTypes || [],
        variants,
      });
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [id, isEdit, navigate, showToast]);

  const change = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const toggleTag = (tag) =>
    setForm(f => ({ ...f, tags: f.tags.includes(tag) ? f.tags.filter(t => t !== tag) : [...f.tags, tag] }));

  // ─── 選項類型挑選 ───────────────────────────────────────────
  const toggleOptionType = (name) =>
    setForm(f => ({
      ...f,
      optionTypes: f.optionTypes.includes(name)
        ? f.optionTypes.filter(n => n !== name)
        : [...f.optionTypes, name],
    }));

  // 可挑的選項類型 = master 啟用的 ∪ 此商品已選的（避免被停用後在編輯頁消失）
  const availableOptionTypes = Array.from(new Set([
    ...optionTypeMaster.filter(o => o.active).map(o => o.name),
    ...form.optionTypes,
  ]));

  // ─── 規格(variant)編輯 ──────────────────────────────────────
  const changeVariant = (idx, key, value) =>
    setForm(f => ({
      ...f,
      variants: f.variants.map((v, i) => (i === idx ? { ...v, [key]: value } : v)),
    }));

  const changeVariantValue = (idx, typeName, value) =>
    setForm(f => ({
      ...f,
      variants: f.variants.map((v, i) =>
        i === idx ? { ...v, values: { ...v.values, [typeName]: value } } : v),
    }));

  const addVariant = () =>
    setForm(f => ({ ...f, variants: [...f.variants, { ...EMPTY_VARIANT, values: {} }] }));

  const removeVariant = (idx) =>
    setForm(f => ({
      ...f,
      variants: f.variants.length > 1 ? f.variants.filter((_, i) => i !== idx) : f.variants,
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { showToast('請輸入商品名稱', 'error'); return; }
    if (form.variants.length === 0) { showToast('至少需要一個規格', 'error'); return; }
    for (const v of form.variants) {
      if (!v.price || Number(v.price) <= 0) { showToast('每個規格的售價必須大於 0', 'error'); return; }
    }

    const payload = {
      name: form.name.trim(),
      brand: form.brand,
      categoryId: form.categoryId,
      description: form.description.trim(),
      detailHtml: form.detailHtml,
      images: form.imagesText.split('\n').map(s => s.trim()).filter(Boolean),
      specs: parseKeyValues(form.specsText),
      tags: form.tags,
      featured: form.featured,
      minLevel: form.minLevel,
      status: form.status,
      optionTypes: form.optionTypes,
      variants: form.variants.map((v, i) => ({
        // attrs 只取此商品有選的選項類型的值
        attrs: form.optionTypes.reduce((acc, name) => {
          const val = (v.values[name] || '').trim();
          if (val) acc[name] = val;
          return acc;
        }, {}),
        price: Number(v.price),
        originalPrice: v.originalPrice ? Number(v.originalPrice) : null,
        stock: Number(v.stock) || 0,
        active: v.active,
        sortOrder: i,
        detailHtml: v.detailHtml || '',
        images: (v.imagesText || '').split('\n').map(s => s.trim()).filter(Boolean),
      })),
    };

    setSubmitting(true);
    try {
      if (isEdit) {
        await adminUpdateProduct(id, payload);
        showToast('商品已更新', 'success');
      } else {
        await adminCreateProduct(payload);
        showToast('商品已建立', 'success');
      }
      navigate('/admin/products');
    } catch (err) {
      showToast(`儲存失敗：${err.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, color: 'var(--ink-soft)' }}>載入商品中…</div>;
  }

  return (
    <>
      <div className="admin-page-head">
        <h1>
          {isEdit ? '編輯商品' : '新增商品'}
          <small>{isEdit ? `EDIT · ${id}` : 'CREATE NEW PRODUCT'}</small>
        </h1>
        <Link to="/admin/products" className="admin-btn ghost">← 返回列表</Link>
      </div>

      <form className="admin-form admin-card" onSubmit={handleSubmit}>
        <div className="field">
          <label>商品名稱 *</label>
          <input
            type="text"
            value={form.name}
            onChange={e => change('name', e.target.value)}
            placeholder="TARGET SERENO G2 23g 鈴木徹 選手款"
          />
        </div>

        <div className="row3">
          <div className="field">
            <label>品牌 *</label>
            <select value={form.brand} onChange={e => change('brand', e.target.value)}>
              {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>分類 *</label>
            <select value={form.categoryId} onChange={e => change('categoryId', e.target.value)}>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name} {c.en}</option>)}
            </select>
          </div>
          <div className="field">
            <label>狀態 *</label>
            <select value={form.status} onChange={e => change('status', e.target.value)}>
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div className="field">
          <label className={`admin-check${form.featured ? ' on' : ''}`}>
            <input
              type="checkbox"
              checked={form.featured}
              onChange={e => change('featured', e.target.checked)}
            />
            設為推薦商品
            <small>首頁推薦區優先顯示</small>
          </label>
        </div>

        <div className="field">
          <label>最低可見等級</label>
          <select value={form.minLevel} onChange={e => change('minLevel', Number(e.target.value))}>
            {levels.map(l => <option key={l.id} value={l.level}>{l.name}</option>)}
          </select>
          <small style={{ display: 'block', marginTop: 6, color: 'var(--ink-soft)', fontSize: 12 }}>
            會員等級 ≥ 這個等級才看得到此商品（門檻制）。預設「一般」= 所有人可見。
          </small>
        </div>

        <div className="field">
          <label>商品描述（短）</label>
          <textarea
            value={form.description}
            onChange={e => change('description', e.target.value)}
            placeholder="SERENO GENERATION 2 · TORU SUZUKI SIGNATURE · SWISS POINT"
          />
          <small style={{ display: 'block', marginTop: 6, color: 'var(--ink-soft)', fontSize: 12 }}>
            列表卡片與詳情頁標題下方的副標，一句話即可。詳細介紹請用下方「商品詳細說明」。
          </small>
        </div>

        <div className="field">
          <label>商品詳細說明</label>
          <small style={{ display: 'block', marginBottom: 8, color: 'var(--ink-soft)', fontSize: 12 }}>
            可放發售資訊、商品介紹、海報圖片（貼網址）、影片連結。顯示在詳情頁的「商品說明」區。
          </small>
          <RichTextEditor
            value={form.detailHtml}
            onChange={(html) => change('detailHtml', html)}
          />
        </div>

        {/* ─── 選項類型 ──────────────────────────────────────── */}
        <div className="field">
          <label>這個商品有哪些選項？</label>
          <small style={{ display: 'block', marginBottom: 10, color: 'var(--ink-soft)', fontSize: 12 }}>
            從「設定 → 選項管理」維護的清單挑選。沒有選項的商品可不選，只填一個規格的價格/庫存。
          </small>
          {availableOptionTypes.length === 0 ? (
            <small style={{ color: 'var(--ink-soft)' }}>
              尚無選項類型，請先到「設定 → 選項管理」新增（如「重量」）。
            </small>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {availableOptionTypes.map(name => (
                <button
                  key={name}
                  type="button"
                  onClick={() => toggleOptionType(name)}
                  style={{
                    padding: '8px 16px', borderRadius: 6,
                    border: '1.5px solid ' + (form.optionTypes.includes(name) ? 'var(--navy)' : 'var(--line)'),
                    background: form.optionTypes.includes(name) ? 'var(--navy)' : 'var(--white)',
                    color: form.optionTypes.includes(name) ? 'var(--white)' : 'var(--ink-strong)',
                    font: '700 13px "Noto Sans TC", sans-serif',
                    cursor: 'pointer',
                  }}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ─── 規格(variant) ─────────────────────────────────── */}
        <div className="field">
          <label>規格與庫存 *</label>
          <small style={{ display: 'block', marginBottom: 10, color: 'var(--ink-soft)', fontSize: 12 }}>
            每個規格各自有售價與庫存（SKU 由系統自動產生）。上面選了選項類型後，這裡會出現對應的值欄位。
          </small>

          {form.variants.map((v, idx) => (
            <div
              key={idx}
              style={{
                border: '1.5px solid var(--line)', borderRadius: 8, padding: 14, marginBottom: 12,
                display: 'flex', flexDirection: 'column', gap: 10,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <b style={{ font: '700 13px "Noto Sans TC"', color: 'var(--navy)' }}>規格 {idx + 1}</b>
                {form.variants.length > 1 && (
                  <button type="button" className="admin-btn danger sm" onClick={() => removeVariant(idx)}>
                    移除
                  </button>
                )}
              </div>

              {form.optionTypes.length > 0 && (
                <div className="row3">
                  {form.optionTypes.map(name => (
                    <div className="field" style={{ margin: 0 }} key={name}>
                      <label style={{ fontSize: 12 }}>{name}</label>
                      <input
                        value={v.values[name] || ''}
                        onChange={e => changeVariantValue(idx, name, e.target.value)}
                        placeholder={name === '重量' ? '23g' : ''}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="row3">
                <div className="field" style={{ margin: 0 }}>
                  <label style={{ fontSize: 12 }}>售價 (NT$) *</label>
                  <input type="number" min="0" step="1" value={v.price}
                    onChange={e => changeVariant(idx, 'price', e.target.value)} />
                </div>
                <div className="field" style={{ margin: 0 }}>
                  <label style={{ fontSize: 12 }}>原價 (NT$，選填)</label>
                  <input type="number" min="0" step="1" value={v.originalPrice}
                    onChange={e => changeVariant(idx, 'originalPrice', e.target.value)} placeholder="無折扣留空" />
                </div>
                <div className="field" style={{ margin: 0 }}>
                  <label style={{ fontSize: 12 }}>庫存 *</label>
                  <input type="number" min="0" step="1" value={v.stock}
                    onChange={e => changeVariant(idx, 'stock', e.target.value)} />
                </div>
              </div>

              <label className={`admin-check${v.active ? ' on' : ''}`}>
                <input
                  type="checkbox"
                  checked={v.active}
                  onChange={e => changeVariant(idx, 'active', e.target.checked)}
                />
                此規格可售
              </label>

              <div className="field" style={{ margin: 0 }}>
                <label style={{ fontSize: 12 }}>此規格專屬圖片（每行一張網址，選填）</label>
                <textarea
                  value={v.imagesText}
                  onChange={e => changeVariant(idx, 'imagesText', e.target.value)}
                  placeholder={'https://example.com/red.jpg'}
                  style={{ fontFamily: 'monospace', fontSize: 13, minHeight: 50 }}
                />
                <small style={{ display: 'block', marginTop: 4, color: 'var(--ink-soft)', fontSize: 12 }}>
                  詳情頁圖庫會一起列出；顧客點到這些圖時，選項自動跳到此規格。留空則此規格沿用商品主圖。
                </small>
                {(v.imagesText || '').split('\n').map(s => s.trim()).filter(Boolean).length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    {(v.imagesText || '').split('\n').map(s => s.trim()).filter(Boolean).map((src, k) => (
                      <div key={k} style={{
                        width: 56, height: 70, border: '1.5px solid var(--line)', borderRadius: 6,
                        display: 'grid', placeItems: 'center', overflow: 'hidden', background: 'var(--ice)',
                      }}>
                        <ProductImage src={src} alt={`規格圖 ${k + 1}`}
                          svgStyle={{ height: '85%' }} imgStyle={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="field" style={{ margin: 0 }}>
                <label style={{ fontSize: 12 }}>此規格專屬說明（選填）</label>
                <small style={{ display: 'block', marginBottom: 8, color: 'var(--ink-soft)', fontSize: 12 }}>
                  例如同系列不同角色卡各自的介紹。詳情頁選到此規格時，會顯示在共用說明下方。
                </small>
                <RichTextEditor
                  value={v.detailHtml}
                  onChange={(html) => changeVariant(idx, 'detailHtml', html)}
                />
              </div>
            </div>
          ))}

          <button type="button" className="admin-btn ghost" onClick={addVariant}>+ 新增規格</button>
        </div>

        <div className="field">
          <label>標籤</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {TAG_OPTIONS.map(t => (
              <button
                key={t.value}
                type="button"
                onClick={() => toggleTag(t.value)}
                style={{
                  padding: '8px 16px', borderRadius: 6,
                  border: '1.5px solid ' + (form.tags.includes(t.value) ? 'var(--navy)' : 'var(--line)'),
                  background: form.tags.includes(t.value) ? 'var(--navy)' : 'var(--white)',
                  color: form.tags.includes(t.value) ? 'var(--white)' : 'var(--ink-strong)',
                  font: '700 13px "Noto Sans TC", sans-serif',
                  cursor: 'pointer',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label>商品圖片（每行一張，貼圖片網址）</label>
          <textarea
            value={form.imagesText}
            onChange={e => change('imagesText', e.target.value)}
            placeholder={'https://example.com/photo1.jpg\nhttps://example.com/photo2.jpg'}
            style={{ fontFamily: 'monospace', fontSize: 13, minHeight: 70 }}
          />
          <small style={{ display: 'block', marginTop: 6, color: 'var(--ink-soft)', fontSize: 12 }}>
            貼圖片網址（https://...），第一張為主圖。
            <br/>也可填內建圖示 id（dart-full / dart-barrel / dart-flight / dart-shaft / dart-tip）作為預設圖。
            <br/>之後會改成直接上傳圖片。
          </small>
        </div>

        {/* 圖片即時預覽 */}
        {form.imagesText.split('\n').map(s => s.trim()).filter(Boolean).length > 0 && (
          <div className="field">
            <label>圖片預覽</label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {form.imagesText.split('\n').map(s => s.trim()).filter(Boolean).map((src, i) => (
                <div key={i} style={{
                  width: 72, height: 90, border: '1.5px solid var(--line)', borderRadius: 6,
                  display: 'grid', placeItems: 'center', overflow: 'hidden', background: 'var(--ice)',
                }}>
                  <ProductImage src={src} alt={`圖 ${i + 1}`}
                    svgStyle={{ height: '85%' }} imgStyle={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="field">
          <label>規格表（一行一個「鍵: 值」）</label>
          <textarea
            value={form.specsText}
            onChange={e => change('specsText', e.target.value)}
            placeholder={'品牌: TARGET DARTS\n重量: 23 g\n材質: 鎢鋼 95%'}
            style={{ fontFamily: 'monospace', fontSize: 13, minHeight: 140 }}
          />
        </div>

        <div className="form-actions">
          <Link to="/admin/products" className="admin-btn ghost">取消</Link>
          <button type="submit" className="admin-btn primary" disabled={submitting}>
            {submitting ? '儲存中…' : (isEdit ? '儲存變更' : '建立商品')}
          </button>
        </div>
      </form>
    </>
  );
}

// 把 "鍵: 值" 多行文字轉成物件（規格表用）
function parseKeyValues(text) {
  const result = {};
  text.split('\n').forEach(line => {
    const idx = line.indexOf(':');
    if (idx === -1) return;
    const key = line.slice(0, idx).trim();
    const value = line.slice(idx + 1).trim();
    if (key && value) result[key] = value;
  });
  return result;
}

export default AdminProductForm;
